<template>
  <div class="utd-verification-code-input">
    <div class="utd-verification-code-input__digit-group">
      <input v-for="i in length"
             :key="i"
             :ref="el => setDigitRef(i - 1, el)"
             :value="digits[i - 1]"
             type="text"
             inputmode="numeric"
             maxlength="1"
             class="utd-verification-code-input__digit"
             :class="{ 'utd-verification-code-input__digit--error': hasError }"
             :aria-label="`Digit ${i} of ${length}`"
             autocomplete="one-time-code"
             @input="handleDigitInput(i - 1, $event)"
             @keydown="handleDigitKeydown(i - 1, $event)"
             @paste="handleDigitPaste">
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    length: {
        type: Number,
        default: 6
    },
    hasError: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits([ 'update:modelValue', 'submit' ]);

const digits = ref(Array(props.length).fill(''));
const digitInputs = [];

const setDigitRef = (index, el) => {
    if (el) {
        digitInputs[index] = el;
    }
};

const handleDigitInput = (index, event) => {
    const value = event.target.value;
    if (value && !/^\d$/.test(value)) {
        digits.value[index] = '';
        event.target.value = '';
        return;
    }
    digits.value[index] = value;
    emit('update:modelValue', digits.value.join(''));
    if (value && index < props.length - 1) {
        nextTick(() => digitInputs[index + 1]?.focus());
    }
};

const handleDigitKeydown = (index, event) => {
    if (event.key === 'Backspace' && !digits.value[index] && index > 0) {
        nextTick(() => digitInputs[index - 1]?.focus());
    }
    if (event.key === 'Enter') {
        emit('submit');
    }
};

const handleDigitPaste = (event) => {
    event.preventDefault();
    const pasted = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, props.length);
    for (let i = 0; i < props.length; i++) {
        digits.value[i] = pasted[i] || '';
    }
    emit('update:modelValue', digits.value.join(''));
    const nextEmpty = digits.value.findIndex(d => !d);
    const focusIdx = nextEmpty === -1 ? props.length - 1 : nextEmpty;
    nextTick(() => digitInputs[focusIdx]?.focus());
};

function reset() {
    digits.value = Array(props.length).fill('');
    emit('update:modelValue', '');
}

function focus() {
    nextTick(() => digitInputs[0]?.focus());
}

watch(() => props.modelValue, (newVal) => {
    const chars = (newVal || '').split('');
    for (let i = 0; i < props.length; i++) {
        digits.value[i] = chars[i] || '';
    }
});

defineExpose({ reset, focus });
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.utd-verification-code-input {
  display: flex;
  justify-content: center;
}

.utd-verification-code-input__digit-group {
  display: flex;
  gap: 13px;
  justify-content: center;
  width: 361px;
  padding: 0;
  box-sizing: border-box;
}

.utd-verification-code-input__digit {
  flex: none;
  width: 48px;
  height: 57px;
  border: 1px solid @WKCE-GRAY-TINT1;
  border-radius: 6px;
  text-align: center;
  font-size: 22px;
  line-height: 57px;
  background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: @DS1-UTD-PRIMARY-LINK-BASE-COLOR;
    box-shadow: 0 0 0 1px @DS1-UTD-PRIMARY-LINK-BASE-COLOR;
  }

  &--error {
    border-color: @WKCE-RED;
  }
}

@media screen and (min-width: 768px) {
  .utd-verification-code-input__digit-group {
    padding: 0;
    gap: 13px;
  }

  .utd-verification-code-input__digit {
    width: 48px;
    height: 57px;
    font-size: 22px;
  }
}
</style>
